/** A selecção vê-se através do modelo.
 *
 *  Seleccionar uma conduta escondida atrás de um tecto falso, ou um
 *  pilar dentro de uma parede, deixa-a pintada mas invisível: a cor da
 *  selecção fica tapada pelo que está à frente. Aqui desenha-se, por
 *  cima de tudo, um véu translúcido e as arestas dos elementos
 *  seleccionados, sem teste de profundidade. O que está à vista fica
 *  como estava (o véu é ténue); o que está tapado passa a ver-se.
 *
 *  Ideia do IFClite (selecção por cima dos oclusores, #5390 no
 *  renderizador deles; MPL-2.0). O código é nosso e usa os triângulos
 *  que o Fragments devolve por elemento (worldTriangles.ts), porque o
 *  Outliner do motor exige trocar o renderizador inteiro.
 *
 *  Os planos de corte aplicam-se sozinhos: o renderizador corta todos
 *  os materiais da cena, incluindo estes.
 *
 *  Limite: até 400 elementos e 600 mil triângulos. Uma selecção maior
 *  (um piso inteiro) não precisa disto e custaria memória. */

import { useEffect } from 'react'
import * as THREE from 'three'

import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { collectWorldTriangles } from '@viewer/lib/worldTriangles'

const COR = '#f59e0b'
const MAX_ELEMENTOS = 400
const MAX_TRIANGULOS = 600_000

export function useSelecaoAtravessa(): void {
  const alvos = useAppStore((s) => s.selectedTargets)
  const ligado = useAppStore((s) => s.selecaoAtravessa)

  useEffect(() => {
    const world = viewerApi.getWorld()
    if (!world || !ligado || alvos.length === 0 || alvos.length > MAX_ELEMENTOS) return
    let cancelado = false
    const grupo = new THREE.Group()
    grupo.name = 'selecao-atravessa'
    const descartaveis: { dispose: () => void }[] = []

    void (async () => {
      const porModelo = new Map<string, number[]>()
      for (const a of alvos) porModelo.set(a.modelId, [...(porModelo.get(a.modelId) ?? []), a.localId])
      let restantes = MAX_TRIANGULOS
      let origem: [number, number, number] | null = null
      const veu = new THREE.MeshBasicMaterial({
        color: COR,
        transparent: true,
        opacity: 0.14,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
      const linhas = new THREE.LineBasicMaterial({ color: COR, transparent: true, opacity: 0.85, depthTest: false })
      descartaveis.push(veu, linhas)
      for (const [modelId, ids] of porModelo) {
        const modelo = viewerApi.getModel(modelId)
        if (!modelo || restantes <= 0) continue
        // Origem perto dos elementos: posições pequenas em Float32, para
        // não ficarem dentadas em modelos georreferenciados.
        if (!origem) {
          try {
            const [caixa] = (await modelo.getBoxes([ids[0]])) as THREE.Box3[]
            const c = caixa && !caixa.isEmpty() ? caixa.getCenter(new THREE.Vector3()) : new THREE.Vector3()
            origem = [c.x, c.y, c.z]
          } catch {
            origem = [0, 0, 0]
          }
        }
        const r = await collectWorldTriangles(modelo as never, { ids, maxTriangles: restantes, origin: origem })
        if (cancelado) return
        restantes -= r.triangles
        for (const item of r.items) {
          const g = new THREE.BufferGeometry()
          g.setAttribute('position', new THREE.BufferAttribute(item.positions, 3))
          g.setIndex(new THREE.BufferAttribute(item.indices, 1))
          const arestas = new THREE.EdgesGeometry(g, 30)
          descartaveis.push(g, arestas)
          const malha = new THREE.Mesh(g, veu)
          const contorno = new THREE.LineSegments(arestas, linhas)
          malha.renderOrder = 998
          contorno.renderOrder = 999
          grupo.add(malha, contorno)
        }
      }
      if (cancelado || !origem) return
      grupo.position.set(origem[0], origem[1], origem[2])
      world.scene.three.add(grupo)
      viewerApi._invalidate()
    })()

    return () => {
      cancelado = true
      grupo.removeFromParent()
      for (const d of descartaveis) d.dispose()
      viewerApi._invalidate()
    }
  }, [alvos, ligado])
}
